import { Injectable, effect, inject, signal } from '@angular/core';
import { ClientSessionService } from '@brighthub/uisession';
import { Subject } from 'rxjs';
import { CarePlanGoal } from '../care-plan.model';

/** Retains only the selected client's completed load across Care Plan tab mounts. */
@Injectable({ providedIn: 'root' })
export class ClientGoalCacheService {
  private readonly session = inject(ClientSessionService);
  private readonly pendingWrites = signal<ReadonlyMap<string, number>>(
    new Map(),
  );
  private readonly settled = new Subject<{ guid: string; clientId: number }>();
  readonly writesSettled = this.settled.asObservable();
  private cached: {
    guid: string;
    clientId: number;
    goals: CarePlanGoal[];
  } | null = null;

  constructor() {
    effect(() => {
      const guid = this.session.selectedSubjectData()?.guid?.trim() || null;
      if (this.cached?.guid !== guid) this.cached = null;
    });
  }

  read(guid: string, clientId: number): CarePlanGoal[] | null {
    return !this.isWriting(guid, clientId) &&
      this.cached?.guid === guid &&
      this.cached.clientId === clientId
      ? this.copy(this.cached.goals)
      : null;
  }

  write(guid: string, clientId: number, goals: readonly CarePlanGoal[]): void {
    if (
      this.isWriting(guid, clientId) ||
      this.session.selectedSubjectData()?.guid?.trim() !== guid
    )
      return;
    this.cached = { guid, clientId, goals: this.copy(goals) };
  }

  invalidate(): void {
    this.cached = null;
  }

  isWriting(guid: string | null, clientId: number | null | undefined): boolean {
    return (
      !!guid && !!clientId && this.pendingWrites().has(`${guid}:${clientId}`)
    );
  }

  /** Settlement survives panel destruction and invalidates replacement consumers. */
  beginWrite(guid: string, clientId: number): () => void {
    const key = `${guid}:${clientId}`;
    this.invalidateClient(guid, clientId);
    this.pendingWrites.update((pending) => {
      const next = new Map(pending);
      next.set(key, (next.get(key) ?? 0) + 1);
      return next;
    });
    let finished = false;
    return () => {
      if (finished) return;
      finished = true;
      this.pendingWrites.update((pending) => {
        const next = new Map(pending);
        const remaining = (next.get(key) ?? 1) - 1;
        if (remaining) next.set(key, remaining);
        else next.delete(key);
        return next;
      });
      this.invalidateClient(guid, clientId);
      this.settled.next({ guid, clientId });
    };
  }

  private invalidateClient(guid: string, clientId: number): void {
    if (this.cached?.guid === guid && this.cached.clientId === clientId)
      this.invalidate();
  }

  private copy(goals: readonly CarePlanGoal[]): CarePlanGoal[] {
    return goals.map((goal) => ({
      ...goal,
      linkedCaseIds: [...goal.linkedCaseIds],
    }));
  }
}
